<template>
  <div
    class="relative  select-from"
    :class="[css]"
  >
    <div
      :class="{open: !isDisplayList, invisible: dataList == null || dataList.length === 0}"
    />
    <div
      v-if="showHeader"
      class="flex pb-2 pt-3 space-out"
    >
      <div class="flex">
        <div
          class="bold"
          :for="'main' + groupId"
        >
          {{ label }}
        </div>
        <template v-if="!isSingleSelection">
          <div class="pl-2">
            |
          </div>
          <div

            class="pl-2 text-sm clickable"
            @click="(e) => { selectAll() }"
          >
            Select All
          </div>
          <div class="pl-2">
            |
          </div>
          <div
            class="pl-2 text-sm clickable"
            @click="unselectAll"
          >
            Clear
          </div>
        </template>
      </div>
      <template v-if="isCollapsable">
        <div
          :class="{'chevron-down-15': isCollapse, 'chevron-15': !isCollapse, }"
          class="clickable pl-2 svg"
          @click="(e) => { isCollapse = !isCollapse }"
        />
      </template>
    </div>
    <Transition name="slide-fade">
      <div v-if="!isCollapse">
        <ul
          class="control sub-selection list"
          :class="{scrollableY: isShowFullList && (displayList!= null && displayList?.length >= maxItemShown)}"
        >
          <template
            v-for="(check, checkIndex) in displayList"
            :key="'check' + checkIndex"
          >
            <li
              :class="[listCss]"
              class="form-control"
            >
              <input
                :id="'category' + check.id"
                v-model="localChecked"
                type="checkbox"
                :value="check.id + ''"
                aria-label=""
                role="checkbox"
                @update:modelValue="() => { updateMaster(check.id + ''); }"
              >
              <label
                class="pl-2"
                :for="'category' + check.id"
              >
                {{ check.name }}
                <div
                  v-show="check.note !== undefined && check.note != null && !isStringNumber(check.note)"
                  class="tooltip-container"
                >
                  <span
                    v-show="check.note !== undefined && check.note != null && !isStringNumber(check.note)"
                    v-tooltip.right="{ content: check.note, theme: 'info-tooltip' }"
                    class="svg info-orange-20"
                    style="cursor: help"
                  />
                </div>
                <span
                  v-show="check.note !== undefined && check.note != null && isStringNumber(check.note)"
                >
                  <span>({{ check.note }})</span>
                </span>
              </label>
            </li>
          </template>
        </ul>
        <div
          v-show="exceedMaxItem"
          class="toggle-label is-brightGreen link"
          @click="toggleFullList"
        >
          {{ isShowFullList ? 'hide' : 'show all' }}
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
/*
this is a simple list of checkbox with a header + select all options
used to use checkbox to do 'select all', 'deselect all'

All value use string, so easy to use array.includes to check values.
*/
import { PropType, computed, ref, watch, watchEffect } from 'vue';
import { OptionType } from '@/config/rawdata.d';

const props = defineProps({
  groupId: { type: String, default: null },
  label: { type: String, default: '' },
  defaultShowList: { type: Boolean, default: true },
  showHeader: { type: Boolean, default: true },
  values: { type: Object as PropType<Array<string>|null|undefined>, default: () => { return []; } },
  dataList: { type: Object as PropType<Array<OptionType> | null>, default: () => { return []; } },
  isSingleSelection: { type: Boolean, default: false },
  isCollapsable: { type: Boolean, default: false },
  isInitExpanded: { type: Boolean, default: false },
  checkEvent: { type: Function, default: () => ({}) },
  maxItemShown: { type: Number, default: 100 },
  css: { type: String, default: '' },
  listCss: { type: String, default: '' },
});
const isDisplayList = ref(props.defaultShowList);
const emit = defineEmits(['update:modelValue', 'update:checkResult']);

const localChecked = ref([...props.values ?? []]); // this will hold the true value. use this to pass back.

const exceedMaxItem = ref((props.dataList ?? []).length > props.maxItemShown);
const isShowFullList = ref(props.isInitExpanded);
const isCollapse = ref(false);

const selectAll = () => {
  localChecked.value = (props.dataList ?? []).map(x => x.id + '');
  emit('update:checkResult', localChecked.value, props.groupId);
};

const unselectAll = () => {
  localChecked.value = []; // Clear all selections
  emit('update:checkResult', localChecked.value, props.groupId);
};
// const selectedOption = computed({
//   // getter
//   get () {
//     return (props.values);
//   },
//   // setter
//   set (newValue: any) {
//     emit('update:modelValue', newValue);
//   }
// });
watch(() => props.values, () => {
  // console.log(props.values, 'updated ');
  localChecked.value = [...props.values ?? []];
}, { deep: true });

watchEffect(() => {
  exceedMaxItem.value = (props.dataList ?? []).length > props.maxItemShown;
});
const displayList = computed(() => {
  if (props.dataList == null || isShowFullList.value || !exceedMaxItem.value) {
    return props.dataList;
  } else {
    return props.dataList.slice(0, props.maxItemShown);
  }
});

const toggleFullList = () => {
  isShowFullList.value = !isShowFullList.value;
};

const isStringNumber = (value: string): boolean => {
  const numericStringRegex = /^[+-]?(\d+\.?\d*|\.\d+)$/;
  return numericStringRegex.test(value);
};

/* set set update selectAll
update:modelValue will be used to do select all in parent component
*/

/* when a value updated, check if all list are selected, if so, trigger event defined for select/ unselect all */
/* localChecked always get the current selected options
id is the current updated value id
when mulitple selection: do nothing, jsut pass back the value list
when single selection, if id is selected (e.g. when it is included in the localChecked list), only keep the last checked one,
otherwise, it is uncheck, do nothing.
*/
const updateMaster = (id: string) => {
  // console.log({ id, opt: localChecked.value, x: props.isSingleSelection });
  if (props.isSingleSelection) {
    if (localChecked.value.includes(id)) {
      localChecked.value = [id];
    }
  }
  emit('update:checkResult', localChecked.value, props.groupId);
  // when all unchecked
  // const unchecked = props.dataList?.findIndex(x => x.checked);
  // if (unchecked === -1) {
  //   emit('update:modelValue', false, props.groupId, props.dataList, id);
  // }

  // // when all checked
  // const checked = props.dataList?.findIndex(x => !x.checked);
  // if (checked === -1) {
  //   emit('update:modelValue', true, props.groupId, props.dataList, id);
  // } else { // only current checked. need to check if other checklist need to be untoggled.
  //   if (isChecked) {
  //     emit('update:modelValue', true, props.groupId, props.dataList, id);
  //   }
  // }
  // console.log({ unchecked, checked });
};

// const completedInputModel = computed({
//   // getter
//   get () {
//     // console.log('...', props.selectAll, (props.selectAll || (props.dataList ?? []).find(x => x.checked) != null));
//     return ((props.dataList ?? []).find(x => x.checked) != null);
//   },
//   // setter
//   set (newValue: boolean) {
//     console.log({ propGroup: props.groupId });
//     emit('update:modelValue', newValue, props.groupId, props.dataList); // select all
//   }
// });

</script>
<style lang="scss" scoped>
.bold {
  font-weight: 700;
}
.list {
  margin-left: 0.3rem;
}
.invisible {
  visibility: hidden;
}
label {
  font-size: 0.8rem;
}
.text-sm {
  font-size: 0.7rem;
}

div.rawdata .tooltip-container {
  position: relative;
  display: inline-block;
}

div.rawdata .tooltip-content {
  visibility: hidden;
  width: 250px;
  background-color: #EFEFEF;
  color: black;
  border: 1px solid #333 !important;
  border-radius: 20px;
  padding: 10px;
  line-height: 1.5rem;

  /* Position the tooltip */
  position: absolute;
  z-index: 3;
  top: -5px; /* Adjust this value to move the tooltip higher or lower relative to the icon */
  // left: 100%;
  left: 10px;
  transform: translateX(10px) translateY(-100%);
  // transform: translateX(-50%);

  /* Opacity for transition */
  opacity: 1;

}

@media screen and (max-width: 1300px) and (min-width:900px){ /* Adjust based on your layout and tooltip size */
  .tooltip-container:hover .tooltip-content {
    // right: 100%; /* Flip to the left side */
    transform: translateX(-150px) translateY(-100%);
  }
}

@media screen and (max-width: 1600px) and (min-width:1400px){ /* Adjust based on your layout and tooltip size */
  .tooltip-container:hover .tooltip-content {
    // right: 100%; /* Flip to the left side */
    transform: translateX(-120px) translateY(-100%);
  }
}

@media screen and (max-width: 1200px) and (min-width:1000px){ /* Adjust based on your layout and tooltip size */
  .tooltip-container:hover .tooltip-content {
    // right: 100%; /* Flip to the left side */
    transform: translateX(-150px) translateY(-100%);
    width: 200px;
  }
}

@media screen and (max-width: 1000px) and (min-width:500px) {  /* Adjust based on your layout and tooltip size */
  .tooltip-container:hover .tooltip-content {
    // right: 100%; /* Flip to the left side */
    transform: translateX(0px) translateY(-100%);
    width: 200px;
  }
}

@media screen and (max-width: 500px) and (min-width:300px) {  /* Adjust based on your layout and tooltip size */
  .tooltip-container:hover .tooltip-content {
    // right: 100%; /* Flip to the left side */
    transform: translateX(-80px) translateY(-100%);
    width: 200px;
  }
}

@media screen and (max-width: 300px){  /* Adjust based on your layout and tooltip size */
  .tooltip-container:hover .tooltip-content {
    // right: 100%; /* Flip to the left side */
    transform: translateX(-45px) translateY(-100%);
    width: 150px;
  }
}

.tooltip-container:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
}

.tooltip-content {
  width: 30px; // New width for the tooltip
  // other styles...
}
/*radio style */
/* Base styles for both types of inputs *
input[type="checkbox"] {
  appearance: none;
  background: #fff;
  border: 1px solid #c5c2c2;
  height: 1.2em;
  width: 1.2em;
  margin: 0 10px 0 5px;
  border-radius: 100%;
  vertical-align: text-bottom;
  position: relative;
  box-shadow: inset 3px 4px 6px 0px #00000024;
}

/* Styles for the pseudo-elements *
input[type="checkbox"]::before {
  content: "";
  position: absolute;
  margin: auto;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  top: 0;
}

/* Center of the checked radio button *
input[type="checkbox"]:checked::before {
  border: 2px solid transparent;
  border-radius: 100%;
  background: #b0bf49;
  margin: 3px;
}

/* Some focus styles for accessibility *
input[type="checkbox"]:focus {
  outline: solid 1px;
  outline-offset: 2px;
  outline-color: #ddd;
}
*/
.scrollableY {
  max-height: 18rem;
  overflow-y: auto;
}
.toggle-label {
  text-align: right;
  padding-right: 1rem;
}
.space-out {
  justify-content: space-between;
}

.tooltip {
  display: block !important;
  z-index: 10000;
}

.tooltip .tooltip-inner {
  background: black;
  color: white;
  border-radius: 16px;
  padding: 5px 10px 4px;
}

.tooltip .tooltip-arrow {
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  margin: 5px;
  border-color: black;
  z-index: 1;
}

.tooltip[x-placement^="top"] {
  margin-bottom: 5px;
}

.tooltip[x-placement^="top"] .tooltip-arrow {
  border-width: 5px 5px 0 5px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  bottom: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}

.tooltip[x-placement^="bottom"] {
  margin-top: 5px;
}

.tooltip[x-placement^="bottom"] .tooltip-arrow {
  border-width: 0 5px 5px 5px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
  top: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}

.tooltip[x-placement^="right"] {
  margin-left: 5px;
}

.tooltip[x-placement^="right"] .tooltip-arrow {
  border-width: 5px 5px 5px 0;
  border-left-color: transparent !important;
  border-top-color: transparent !important;
  border-bottom-color: transparent !important;
  left: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

.tooltip[x-placement^="left"] {
  margin-right: 5px;
}

.tooltip[x-placement^="left"] .tooltip-arrow {
  border-width: 5px 0 5px 5px;
  border-top-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  right: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

.tooltip.popover .popover-inner {
  background: #f9f9f9;
  color: black;
  padding: 24px;
  border-radius: 5px;
  box-shadow: 0 5px 30px rgba(black, .1);
}

.tooltip.popover .popover-arrow {
  border-color: #f9f9f9;
}

.tooltip[aria-hidden='true'] {
  visibility: hidden;
  opacity: 0;
  transition: opacity .15s, visibility .15s;
}

.tooltip[aria-hidden='false'] {
  visibility: visible;
  opacity: 1;
  transition: opacity .15s;
}
</style>
